import { useEffect, useMemo, useState } from 'react';
import { History, Loader2, Upload, Archive, RotateCcw, Layers, Trash2, Wrench, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminAPI } from '@/lib/admin-api';
// Side-effect imports below register the maintenance helpers
// (`AdminAPI.rules.remove`, `AdminAPI.rules.repair`) onto the singleton.
import '@/lib/admin-api-quality';
import { formatDateTime, formatDistanceToNow } from '@/lib/format-time';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STATUS_BADGE = {
  active: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  draft: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  archived: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
};

// Returns a list of { type: 'same'|'add'|'del', text }
const diffLines = (a, b) => {
  const al = a.split('\n');
  const bl = b.split('\n');
  const out = [];
  let i = 0, j = 0;
  while (i < al.length || j < bl.length) {
    if (i >= al.length) { out.push({ type: 'add', text: bl[j++] }); continue; }
    if (j >= bl.length) { out.push({ type: 'del', text: al[i++] }); continue; }
    if (al[i] === bl[j]) { out.push({ type: 'same', text: al[i] }); i++; j++; continue; }
    // lookahead: simple sync — try to find current B line in upcoming A or vice versa
    const aFwd = al.slice(i, i + 5).indexOf(bl[j]);
    const bFwd = bl.slice(j, j + 5).indexOf(al[i]);
    if (aFwd !== -1 && (bFwd === -1 || aFwd <= bFwd)) {
      for (let k = 0; k < aFwd; k++) out.push({ type: 'del', text: al[i + k] });
      i += aFwd;
    } else if (bFwd !== -1) {
      for (let k = 0; k < bFwd; k++) out.push({ type: 'add', text: bl[j + k] });
      j += bFwd;
    } else {
      out.push({ type: 'del', text: al[i++] });
      out.push({ type: 'add', text: bl[j++] });
    }
  }
  return out;
};

const lineClass = (t) => t === 'add'
  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-l-2 border-emerald-500/50'
  : t === 'del'
    ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-l-2 border-rose-500/50 line-through opacity-80'
    : 'text-muted-foreground border-l-2 border-transparent';

export const RuleVersions = ({ cardId, versions, activeVersionId, draftVersionId, currentVersionId, onSelectVersion, onChange }) => {
  const [diffA, setDiffA] = useState(null);
  const [diffB, setDiffB] = useState(null);
  const [diffPayload, setDiffPayload] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [busy, setBusy] = useState(null);

  // Default diff selection: active vs latest draft if available; else 2 most recent
  useEffect(() => {
    if (versions.length < 2) return;
    if (!diffA || !diffB) {
      const active = versions.find((v) => v.status === 'active');
      const latestDraft = versions.find((v) => v.status === 'draft');
      if (active && latestDraft) {
        setDiffA(active.id);
        setDiffB(latestDraft.id);
      } else {
        setDiffA(versions[1]?.id);
        setDiffB(versions[0]?.id);
      }
    }
  }, [versions]); // eslint-disable-line

  useEffect(() => {
    if (!diffA || !diffB) return;
    setLoadingDiff(true);
    AdminAPI.rules.diff(cardId, diffA, diffB)
      .then(setDiffPayload)
      .catch(() => setDiffPayload(null))
      .finally(() => setLoadingDiff(false));
  }, [cardId, diffA, diffB]);

  const aJson = useMemo(() => diffPayload ? JSON.stringify({ rules: diffPayload.a.rules, milestones: diffPayload.a.milestones }, null, 2) : '', [diffPayload]);
  const bJson = useMemo(() => diffPayload ? JSON.stringify({ rules: diffPayload.b.rules, milestones: diffPayload.b.milestones }, null, 2) : '', [diffPayload]);
  const lines = useMemo(() => (aJson && bJson) ? diffLines(aJson, bJson) : [], [aJson, bJson]);

  const publish = async (vid) => {
    setBusy(vid);
    try {
      await AdminAPI.rules.publish(vid);
      toast.success('Version published. Consumer recommendations now use this version.');
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Publish failed');
    } finally { setBusy(null); }
  };
  const archive = async (vid) => {
    setBusy(vid);
    try {
      await AdminAPI.rules.archive(vid);
      toast.success('Version archived');
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Archive failed');
    } finally { setBusy(null); }
  };
  const rollback = async (vid) => {
    setBusy(vid);
    try {
      const v = await AdminAPI.rules.rollback(vid);
      toast.success(`Rolled back — new active version is v${v.version}.`);
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Rollback failed');
    } finally { setBusy(null); }
  };

  // -- Delete (draft / archived only) --
  const [pendingDelete, setPendingDelete] = useState(null);  // version row pending confirm
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const vid = pendingDelete.id;
    setBusy(vid);
    try {
      await AdminAPI.rules.remove(vid);
      toast.success(`Deleted v${pendingDelete.version} (${pendingDelete.status}).`);
      setPendingDelete(null);
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally { setBusy(null); }
  };

  // -- Repair (auto-fix invariant violations) --
  const [repairing, setRepairing] = useState(false);
  const activeCount = versions.filter((v) => v.status === 'active').length;
  const versionNumberCounts = versions.reduce((acc, v) => {
    const n = v.version || 0;
    acc[n] = (acc[n] || 0) + 1;
    return acc;
  }, {});
  const hasDuplicateNumbers = Object.values(versionNumberCounts).some((c) => c > 1);
  const needsRepair = activeCount > 1 || hasDuplicateNumbers;

  const runRepair = async () => {
    setRepairing(true);
    try {
      const report = await AdminAPI.rules.repair(cardId);
      if (report?.changed) {
        const actionList = (report.actions || []).map((a) => a.type).join(', ') || 'changes applied';
        toast.success(`Repair complete — ${actionList}.`);
      } else {
        toast.success('Repair complete — no changes needed.');
      }
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div data-testid="admin-rule-diff-pane" className="flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">Versions &amp; diff</div>
          <Badge variant="outline" className="text-[10px] gap-1"><History className="h-3 w-3" /> {versions.length}</Badge>
          {activeCount > 1 && (
            <Badge variant="outline" className="text-[10px] gap-1 bg-rose-500/10 text-rose-600 border-rose-500/40" data-testid="admin-rule-versions-active-warning">
              <AlertTriangle className="h-3 w-3" /> {activeCount} active
            </Badge>
          )}
        </div>
        {needsRepair && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
            onClick={runRepair}
            disabled={repairing}
            data-testid="admin-rule-versions-repair-button"
            title="Auto-fix duplicate actives and re-sequence version numbers"
          >
            {repairing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />}
            Repair
          </Button>
        )}
      </div>

      {/* Banner explaining the repair recommendation */}
      {needsRepair && (
        <div
          className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-700 dark:text-amber-300 flex items-start gap-2"
          data-testid="admin-rule-versions-repair-banner"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            This card has{' '}
            {activeCount > 1 && <strong>{activeCount} versions marked active</strong>}
            {activeCount > 1 && hasDuplicateNumbers && ' and '}
            {hasDuplicateNumbers && <strong>duplicate version numbers</strong>}
            . Run <em>Repair</em> to consolidate to a single active version and re-sequence numbers — the card pointer is preserved.
          </div>
        </div>
      )}

      {/* Version list — uses a native overflow-y-auto wrapper. We
          deliberately moved away from the Radix ScrollArea here because
          its `max-h-*` mode would only swap in a custom (hard-to-spot)
          thin scrollbar; with ≥7 versions it became impossible to scroll
          past v3. Native scroll guarantees a visible scrollbar AND
          keyboard / wheel scrolling works in every browser. */}
      <Card className="p-2 mb-3">
        <div
          className="max-h-[260px] overflow-y-auto scrollbar-thin pr-1"
          data-testid="admin-rule-version-list-scroll"
        >
          <div className="space-y-1">
            {versions.map((v) => {
              const selected = v.id === currentVersionId;
              const isCardPointer = v.id === activeVersionId;
              const canDelete = v.status === 'draft' || (v.status === 'archived' && !isCardPointer);
              return (
                <div key={v.id} className={cn('rounded-md border px-2.5 py-1.5 flex items-center gap-2 text-xs cursor-pointer', selected ? 'border-accent bg-accent/10' : 'border-border hover:bg-muted/40')} onClick={() => onSelectVersion?.(v.id)} data-testid={`admin-rule-version-row-${v.id}`}>
                  <div className="font-mono-num font-semibold w-9 text-right">v{v.version}</div>
                  <Badge variant="outline" className={cn('text-[10px]', STATUS_BADGE[v.status])}>{v.status}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-[11px]">{v.notes || '—'}</div>
                    <div className="text-[10px] text-muted-foreground">{v.created_by || 'unknown'} · {formatDistanceToNow(v.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                    {v.status === 'draft' && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => publish(v.id)} disabled={busy === v.id} data-testid={`admin-rule-publish-${v.id}`} title="Publish">
                        {busy === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {v.status === 'draft' && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-rose-500" onClick={() => archive(v.id)} disabled={busy === v.id} data-testid={`admin-rule-archive-${v.id}`} title="Archive draft">
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {v.status === 'archived' && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-accent" onClick={() => rollback(v.id)} disabled={busy === v.id} data-testid={`admin-rule-rollback-${v.id}`} title="Rollback to this">
                        {busy === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-rose-500"
                        onClick={() => setPendingDelete(v)}
                        disabled={busy === v.id}
                        data-testid={`admin-rule-delete-${v.id}`}
                        title={`Delete this ${v.status} version`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="admin-rule-version-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete v{pendingDelete?.version} ({pendingDelete?.status})?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the rule version. The card&apos;s currently-active
              pointer is unaffected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-rule-version-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="admin-rule-version-delete-confirm"
            >
              {busy === pendingDelete?.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diff */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Layers className="h-3 w-3 text-muted-foreground" />
        <Select value={diffA || ''} onValueChange={setDiffA}>
          <SelectTrigger className="h-7 w-[120px] text-xs" data-testid="admin-rule-diff-version-a"><SelectValue placeholder="From" /></SelectTrigger>
          <SelectContent>{versions.map((v) => <SelectItem key={v.id} value={v.id}>v{v.version} · {v.status}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">→</span>
        <Select value={diffB || ''} onValueChange={setDiffB}>
          <SelectTrigger className="h-7 w-[120px] text-xs" data-testid="admin-rule-diff-version-b"><SelectValue placeholder="To" /></SelectTrigger>
          <SelectContent>{versions.map((v) => <SelectItem key={v.id} value={v.id}>v{v.version} · {v.status}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="p-2 flex-1 overflow-hidden" data-testid="admin-rule-diff-view">
        {loadingDiff ? (
          <div className="p-6 text-center text-muted-foreground text-xs"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
        ) : !diffPayload || lines.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-xs" data-testid="admin-rule-diff-empty">
            Select two versions to compare — changes highlight here.
          </div>
        ) : (
          <div
            className="max-h-[480px] overflow-y-auto scrollbar-thin pr-1"
            data-testid="admin-rule-diff-scroll"
          >
            <pre className="font-mono-num text-[11px] leading-relaxed">
              {lines.map((l, i) => (
                <div key={i} className={cn('px-2 py-0.5 whitespace-pre-wrap break-all', lineClass(l.type))}>
                  <span className="select-none mr-2 opacity-50">{l.type === 'add' ? '+' : l.type === 'del' ? '−' : ' '}</span>
                  {l.text}
                </div>
              ))}
            </pre>
          </div>
        )}
      </Card>
    </div>
  );
};
